import "draft-js/dist/Draft.css";
import "./style.scss";

import AddIcon from "@mui/icons-material/Add";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import { Button, IconButton } from "@mui/material";
import type { RawDraftContentState } from "draft-js";
import { ContentState, convertFromRaw, convertToRaw, Editor, EditorState, RichUtils } from "draft-js";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export const inlineStyles = [
    { style: "BOLD", icon: <FormatBoldIcon /> },
    { style: "ITALIC", icon: <FormatItalicIcon /> },
    { style: "UNDERLINE", icon: <FormatUnderlinedIcon /> },
    { style: "STRIKETHROUGH", icon: <StrikethroughSIcon /> },
    // { style: "FONT_SIZE_30", icon: <TextIncreaseIcon /> }
];

const customStyleMap = {
    STRIKETHROUGH: {
        textDecoration: "line-through",
    },
    FONT_SIZE_30: {
        fontSize: "30px",
    },
};

interface RichEditorProps {
    onChange: (data: RawDraftContentState) => void;
    readOnly?: boolean;
    readOnlyEditorState?: RawDraftContentState;
}

const RichEditor = ({ onChange, readOnly, readOnlyEditorState }: RichEditorProps) => {
    const [t] = useTranslation();
    const readOnlyContentState = (readOnly && readOnlyEditorState && convertFromRaw(readOnlyEditorState)) || undefined;
    const [editorState, setEditorState] = useState(
        () =>
            (readOnly &&
                readOnlyEditorState &&
                readOnlyContentState &&
                EditorState.createWithContent(readOnlyContentState)) ||
            EditorState.createEmpty(),
    );
    const editorRef: any = useRef(null);
    const [isDirty, setIsDirty] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    const handleInlineStyle = (style: string) => setEditorState(RichUtils.toggleInlineStyle(editorState, style));

    const onEditorChange = (e: any) => {
        setEditorState(e);
        setIsDirty(!!e.getCurrentContent().getPlainText()); // this only gets raw text
        // const value = blocks.map(block => (!block.text.trim() && '\n') || block.text).join('\n'); // raw text
    };

    const handleEditorSave = () => {
        // onChange(convertToRaw(editorState.getCurrentContent()).blocks) // this gets an object with inline styles data and text etc
        onChange(convertToRaw(editorState.getCurrentContent()));
        setEditorState(EditorState.push(editorState, ContentState.createFromText(""), "remove-range"));
        setIsDirty(false);
    };

    const handleBlur = (e: any) => {
        const currentTarget = e.currentTarget;
        setTimeout(() => {
            if (!currentTarget.contains(document.activeElement)) {
                setIsFocused(false);
            }
        }, 0);
    };

    useEffect(() => {
        setEditorState(
            () =>
                (readOnly &&
                    readOnlyEditorState &&
                    readOnlyContentState &&
                    EditorState.createWithContent(readOnlyContentState)) ||
                EditorState.createEmpty(),
        );
    }, [readOnlyEditorState]);

    return (
        <div className={(readOnly && "readOnlyEditor") || "editor"} onBlur={handleBlur}>
            {(!readOnly && (
                <>
                    <div className="textModifiers">
                        <div className="inlineButtons">
                            {inlineStyles.map((item, i) => (
                                <IconButton
                                    onClick={() => handleInlineStyle(item.style)}
                                    color={editorState.getCurrentInlineStyle().has(item.style) ? "primary" : "default"}
                                    key={i}
                                >
                                    {item.icon}
                                </IconButton>
                            ))}
                        </div>
                        {isDirty && (
                            <div className="addNote">
                                <Button startIcon={<AddIcon />} onClick={handleEditorSave}>
                                    {t("RichEditor.AddNote")}
                                </Button>
                            </div>
                        )}
                    </div>
                    <div className="textEditor" style={(!isFocused && { minHeight: 0 }) || {}}>
                        <Editor
                            editorState={editorState}
                            onChange={onEditorChange}
                            onFocus={() => setIsFocused(true)}
                            customStyleMap={customStyleMap}
                            placeholder={t("RichEditor.StartWriting")}
                            ref={editorRef}
                        />
                    </div>
                </>
            )) ||
                (readOnly && readOnlyEditorState && (
                    <div className="readOnlyTextEditor">
                        <Editor readOnly={true} editorState={editorState} onChange={() => {}} />
                    </div>
                ))}
        </div>
    );
};

export default RichEditor;
